import { type ReactNode, useEffect, useState } from "react"

import { ChevronDown, ChevronRight } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"

import { normalizeFolderPath } from "./library-editor-model"

type FieldRowProps = {
  id: string
  label: string
  required?: boolean
  help?: string
  error?: string
  children: ReactNode
}

/** Label + control + help + error, the one layout every field in the editor uses. */
export function FieldRow({ id, label, required, help, error, children }: FieldRowProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      {children}
      {help && <p className="text-muted-foreground text-xs">{help}</p>}
      {error && <p className="text-destructive text-sm">{error}</p>}
    </div>
  )
}

type SwitchRowProps = {
  id: string
  label: string
  help?: string
  checked: boolean
  disabled?: boolean
  onCheckedChange: (checked: boolean) => void
}

export function SwitchRow({ id, label, help, checked, disabled, onCheckedChange }: SwitchRowProps) {
  return (
    <div className="flex items-center justify-between rounded-lg border p-3">
      <div className="space-y-0.5 pr-4">
        <Label htmlFor={id}>{label}</Label>
        {help && <p className="text-muted-foreground text-xs">{help}</p>}
      </div>
      <Switch checked={checked} disabled={disabled} id={id} onCheckedChange={onCheckedChange} />
    </div>
  )
}

type CsvInputProps = {
  id: string
  values: string[]
  placeholder?: string
  onChange: (values: string[]) => void
}

function parseCsv(text: string): string[] {
  return text
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean)
}

function sameList(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((entry, index) => entry === b[index])
}

/**
 * Comma-separated free text kept as a string array in the config. The raw text lives here so a
 * trailing comma or space survives while typing; only the parsed list goes up. When the parent
 * replaces the list (dialog reopen, source type reset) the text follows it.
 */
export function CsvInput({ id, values, placeholder, onChange }: CsvInputProps) {
  const [text, setText] = useState(() => values.join(", "))

  useEffect(() => {
    setText((current) => (sameList(parseCsv(current), values) ? current : values.join(", ")))
  }, [values])

  return (
    <Input
      id={id}
      placeholder={placeholder}
      value={text}
      onBlur={() => setText(values.join(", "))}
      onChange={(event) => {
        setText(event.target.value)
        onChange(parseCsv(event.target.value))
      }}
    />
  )
}

type FolderPathInputProps = {
  id: string
  value: string
  placeholder?: string
  onChange: (value: string) => void
}

/**
 * A folder path kept as raw text while typing (a trailing slash has to survive to reach the next
 * segment); only the normalised path goes up, and blur tidies the text to it.
 */
export function FolderPathInput({ id, value, placeholder, onChange }: FolderPathInputProps) {
  const [text, setText] = useState(value)

  useEffect(() => {
    setText((current) => (normalizeFolderPath(current) === value ? current : value))
  }, [value])

  return (
    <Input
      id={id}
      placeholder={placeholder}
      value={text}
      onBlur={() => setText(value)}
      onChange={(event) => {
        setText(event.target.value)
        onChange(normalizeFolderPath(event.target.value))
      }}
    />
  )
}

type EditorSectionCardProps = {
  id: string
  title: string
  icon?: ReactNode
  badge?: ReactNode
  description?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
}

/** Collapsible section: full-width outline trigger with title, optional icon and badge. */
export function EditorSectionCard({
  id,
  title,
  icon,
  badge,
  description,
  open,
  onOpenChange,
  children,
}: EditorSectionCardProps) {
  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <CollapsibleTrigger asChild>
        <Button
          aria-controls={`${id}-content`}
          className="w-full justify-between"
          type="button"
          variant="outline"
        >
          <span className="flex items-center gap-2">
            {icon}
            {title}
            {badge}
          </span>
          {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-4 pt-3" id={`${id}-content`}>
        {description && <p className="text-muted-foreground text-xs">{description}</p>}
        {children}
      </CollapsibleContent>
    </Collapsible>
  )
}
